import { Box, Checkbox, Flex, Input, Radio, RadioGroup, Stack, Text, VStack } from '@chakra-ui/react'

import { ActionButton } from '../../../components/ActionButton'
import { FormField } from '../../../components/FormField'
import { JobRequisitionScheduleDay, WeekDay, WeekendMode } from '../../../services/JobRequisition/types'
import { WEEK_DAYS, WEEKEND_DAYS, WEEKEND_MODE_LABELS } from './constants'

interface IJobRequisitionScheduleEditorProps {
  /** Worked days, in week order: the index is the one the API errors use (`schedule.0.start`). */
  value: JobRequisitionScheduleDay[]
  onChange: (value: JobRequisitionScheduleDay[]) => void
  fieldErrors: Record<string, string[]>
  isDisabled?: boolean
  isReadOnly?: boolean
}

const DAY_LABELS = Object.fromEntries(WEEK_DAYS.map((day) => [day.value, day.label])) as Record<WeekDay, string>

const isWeekend = (day: WeekDay) => WEEKEND_DAYS.includes(day)

const emptyDay = (day: WeekDay): JobRequisitionScheduleDay => ({
  day,
  start: '',
  end: '',
  break: '',
  weekend_mode: null
})

/** "Segunda-feira: 09:00 às 18:00, intervalo de 01:00" — and, on the weekend, fixed or alternate. */
export const describeScheduleDay = (day: JobRequisitionScheduleDay) =>
  `${DAY_LABELS[day.day]}: ${day.start} às ${day.end}, intervalo de ${day.break}` +
  (day.weekend_mode ? ` (${WEEKEND_MODE_LABELS[day.weekend_mode].toLowerCase()})` : '')

/**
 * Working days of the RP and, for each one, its hours and break. The client's document asks
 * Saturday and Sunday whether they are alternate or fixed, each on its own.
 */
export function JobRequisitionScheduleEditor({
  value,
  onChange,
  fieldErrors,
  isDisabled = false,
  isReadOnly = false
}: IJobRequisitionScheduleEditorProps) {
  if (isReadOnly) {
    return value.length === 0 ? (
      <Text variant="caption">Nenhum dia informado</Text>
    ) : (
      <VStack align="stretch" spacing={1}>
        {value.map((day) => (
          <Text key={day.day}>{describeScheduleDay(day)}</Text>
        ))}
      </VStack>
    )
  }

  const toggleDay = (day: WeekDay, checked: boolean) => {
    const days = checked ? [...value, emptyDay(day)] : value.filter((item) => item.day !== day)
    const order = WEEK_DAYS.map((weekDay) => weekDay.value)
    onChange(days.sort((a, b) => order.indexOf(a.day) - order.indexOf(b.day)))
  }

  const updateDay = (day: WeekDay, patch: Partial<JobRequisitionScheduleDay>) =>
    onChange(value.map((item) => (item.day === day ? { ...item, ...patch } : item)))

  /** Copies the first day's hours to the other selected days; each one stays editable. */
  const repeatFirstDay = () => {
    const [first] = value
    onChange(value.map((item) => ({ ...item, start: first.start, end: first.end, break: first.break })))
  }

  const errorOf = (index: number, field: keyof JobRequisitionScheduleDay) => fieldErrors[`schedule.${index}.${field}`]

  return (
    <Stack spacing={0} borderWidth="1px" borderRadius="md" divider={<Box borderBottomWidth="1px" />}>
      {WEEK_DAYS.map(({ value: day, label }) => {
        const index = value.findIndex((item) => item.day === day)
        const selected = index >= 0 ? value[index] : null

        return (
          <Flex key={day} p={3} gap={4} wrap="wrap" align="flex-start">
            <Checkbox
              isChecked={selected !== null}
              onChange={(e) => toggleDay(day, e.target.checked)}
              isDisabled={isDisabled}
              minW="140px"
              mt={selected ? 8 : 0}
            >
              {label}
            </Checkbox>

            {selected && (
              <>
                <FormField label="Entrada" error={errorOf(index, 'start')} isRequired w="130px">
                  <Input
                    type="time"
                    value={selected.start}
                    onChange={(e) => updateDay(day, { start: e.target.value })}
                    isDisabled={isDisabled}
                  />
                </FormField>
                <FormField label="Saída" error={errorOf(index, 'end')} isRequired w="130px">
                  <Input
                    type="time"
                    value={selected.end}
                    onChange={(e) => updateDay(day, { end: e.target.value })}
                    isDisabled={isDisabled}
                  />
                </FormField>
                <FormField label="Intervalo" error={errorOf(index, 'break')} isRequired w="130px">
                  <Input
                    type="time"
                    value={selected.break}
                    onChange={(e) => updateDay(day, { break: e.target.value })}
                    isDisabled={isDisabled}
                  />
                </FormField>
                {isWeekend(day) && (
                  <FormField label="Intercalado ou fixo" error={errorOf(index, 'weekend_mode')} isRequired w="auto">
                    <RadioGroup
                      value={selected.weekend_mode ?? ''}
                      onChange={(mode) => updateDay(day, { weekend_mode: mode as WeekendMode })}
                      isDisabled={isDisabled}
                    >
                      <Stack direction="row" spacing={4} h="40px" align="center">
                        {(Object.keys(WEEKEND_MODE_LABELS) as WeekendMode[]).map((mode) => (
                          <Radio key={mode} value={mode}>
                            {WEEKEND_MODE_LABELS[mode]}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </FormField>
                )}
                {index === 0 && value.length > 1 && (
                  <ActionButton
                    action="duplicate"
                    label="Repetir nos outros dias"
                    showLabel
                    variant="outline"
                    mt={8}
                    isDisabled={isDisabled || !selected.start || !selected.end || !selected.break}
                    onClick={repeatFirstDay}
                  />
                )}
              </>
            )}
          </Flex>
        )
      })}
    </Stack>
  )
}
